/**
 * Date helpers and pricing utilities.
 */

export function parseISODate(s) {
    if (!s) return null;
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
}

export function formatISO(date) {
    if (!date) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

export function nightsBetween(check_in, check_out) {
    if (!check_in || !check_out) return 0;
    const a = parseISODate(check_in);
    const b = parseISODate(check_out);
    if (!a || !b) return 0;
    return Math.max(0, Math.round((b - a) / (1000 * 60 * 60 * 24)));
}

/**
 * Build a list of nights between check_in and check_out (exclusive of check_out).
 * Returns array of Date objects.
 */
export function eachNight(check_in, check_out) {
    const out = [];
    const start = parseISODate(check_in);
    const end = parseISODate(check_out);
    if (!start || !end) return out;
    const cur = new Date(start);
    while (cur < end) {
        out.push(new Date(cur));
        cur.setDate(cur.getDate() + 1);
    }
    return out;
}

/**
 * Check if a Date falls within a yearly "MM-DD" → "MM-DD" range.
 * Handles ranges that cross year boundary (e.g. Dec 20 → Jan 5).
 */
export function dateInSeasonalRange(date, startMMDD, endMMDD) {
    if (!startMMDD || !endMMDD) return false;
    const m = date.getMonth() + 1;
    const d = date.getDate();
    const cur = m * 100 + d;
    const [sm, sd] = startMMDD.split("-").map(Number);
    const [em, ed] = endMMDD.split("-").map(Number);
    const s = sm * 100 + sd;
    const e = em * 100 + ed;
    if (s <= e) return cur >= s && cur <= e;
    // wraps year boundary
    return cur >= s || cur <= e;
}

/**
 * Compute total price for a stay given a room and the dates.
 * For each night, picks the seasonal price if it applies, else the default price.
 */
export function computePrice(room, check_in, check_out) {
    if (!room || !check_in || !check_out) return { total: 0, nights: 0, breakdown: [] };
    const nights = eachNight(check_in, check_out);
    let total = 0;
    const breakdown = [];
    for (const night of nights) {
        let price = room.price;
        let label = "default";
        for (const sp of room.seasonal_prices || []) {
            if (dateInSeasonalRange(night, sp.start_date, sp.end_date)) {
                price = sp.price;
                label = sp.label_en || sp.label_al || "seasonal";
                break;
            }
        }
        total += price;
        breakdown.push({ date: formatISO(night), price, label });
    }
    return { total, nights: nights.length, breakdown };
}

/**
 * Build a set of unavailable dates from booking ranges.
 * Returns Set of ISO date strings (the nights that are taken).
 */
export function unavailableDateSet(ranges) {
    const s = new Set();
    for (const r of ranges || []) {
        for (const night of eachNight(r.check_in, r.check_out)) {
            s.add(formatISO(night));
        }
    }
    return s;
}

/**
 * Compute the total for a single add-on given its price_type, nights and guests.
 * price_type ∈ "flat" | "per_night" | "per_person" | "per_person_night"
 */
export function computeAddonTotal(addon, nights, guests) {
    if (!addon) return 0;
    const n = Math.max(0, nights || 0);
    const g = Math.max(1, guests || 1);
    const p = Number(addon.price) || 0;
    switch (addon.price_type) {
        case "per_night":
            return p * n;
        case "per_person":
            return p * g;
        case "per_person_night":
            return p * n * g;
        case "flat":
        default:
            return p;
    }
}
